import { TAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import pako from 'pako'

import { NotificationController } from 'core-components/library/popup'
import { getTokens } from 'core/classes/helpers/StorageHelper'
import Logger from 'core/utils/Logger'
import { mapGeoMappingItem } from 'partner360/components/filters'
import { createNetworkDebugRecorder, readNetworkDebugText } from 'shared/lib/networkDebug'
import settings from 'src/config/settings'

import {
  DEFAULT_CURRENCY,
  getAvailableCockpitCurrencies,
  getPartnerOfferingsCatalog,
  mapCockpitMdfActivityFromApi,
  mapCockpitPartnerFromApi,
  mapPartnerFinSobRowFromApi,
  parseCockpitProgramOfferings,
} from '../lib'
import {
  downloadFailed,
  processingStarted,
  requestingStarted,
  resetDownload,
} from '../model/actions'
import { downloadWithProgress } from '../model/thunks/downloadWithProgress'

import {
  IFetchFinancialCockpitDataParams,
  IFinancialCockpitData,
  IFinancialCockpitDataApi,
  IFinancialCockpitResponse,
} from './types'

const EMPTY_FINANCIAL_COCKPIT_DATA: IFinancialCockpitData = {
  UserInfo: null,
  GeoMapping: [],
  PlanningCycles: [],
  PartnerFinSobData: [],
  CommitmentData: [],
  CompletenessData: [],
  CommitmentStatus: [],
  PartnerData: [],
  ActualsAndTargetsSettings: [],
  MDFActivities: [],
  MDFSales: [],
  KeyInitiatives: [],
  PBMActions: [],
  Users: [],
  Offerings: [],
  PartnerOfferingsCatalog: [],
  AvailableCurrencies: [DEFAULT_CURRENCY],
  ELTDocuments: [],
  QBRDocuments: [],
  PartnerFlags: [],
  PartnerFlagRequests: [],
  PartnerFlagApprovers: [],
}

interface IFetchFinancialCockpitDataArgs extends IFetchFinancialCockpitDataParams {
  dispatch: TAppDispatch
  /** Records the request, its raw response and the mapped result for the Super Admin's
   *  network data window. */
  debug?: boolean
}

/** A joined cockpit response can repeat the same initiative. EntryId is the initiative identity,
 * so collapse those duplicate rows before any tile counts or aggregates them. */
const deduplicateKeyInitiatives = (
  groups: IFinancialCockpitDataApi['KeyInitiatives']
): IFinancialCockpitData['KeyInitiatives'] =>
  groups.map((group) => {
    const entryIds = new Set<number>()
    const statsByYear = new Map<
      number,
      { totalCount: number; duplicateCount: number; duplicateEntryIds: Set<number> }
    >()

    const keyInitiatives = group.KeyInitiatives.filter(({ EntryId, FYear }) => {
      const stats = statsByYear.get(FYear) ?? {
        totalCount: 0,
        duplicateCount: 0,
        duplicateEntryIds: new Set<number>(),
      }
      stats.totalCount += 1
      statsByYear.set(FYear, stats)

      if (entryIds.has(EntryId)) {
        stats.duplicateCount += 1
        stats.duplicateEntryIds.add(EntryId)
        return false
      }

      entryIds.add(EntryId)
      return true
    })

    statsByYear.forEach(({ totalCount, duplicateCount, duplicateEntryIds }, fYear) => {
      if (duplicateCount === 0) return

      const duplicateIds = [...duplicateEntryIds].sort((a, b) => a - b).join(', ')
      Logger.error(
        `Financial cockpit KeyInitiatives data error for partner ${group.PartnerId}, FY${fYear}: duplicate EntryIds ${duplicateIds}; ${totalCount} records, ${totalCount - duplicateCount} unique.`
      )
    })

    return {
      ...group,
      KeyInitiatives: keyInitiatives,
    }
  })

/** Maps the wire payload to the shape the rest of the app consumes — Fin/SOB rows and MDF
 *  activities mapped, offerings parsed, and every optional array field resolved to `[]`. Kept
 *  as a standalone, pure function (no `fetch`/gzip/dispatch involved) so mapping behavior —
 *  including the `?? []` fallbacks — is unit-testable without mocking the network layer. */
export const mapFinancialCockpitDataFromApi = (
  data: IFinancialCockpitDataApi
): IFinancialCockpitData => ({
  UserInfo: data.UserInfo?.[0] ?? null,
  GeoMapping: (data.GeoMapping ?? []).map(mapGeoMappingItem).sort((a, b) => a.order - b.order),
  PlanningCycles: data.PlanningCycles ?? [],
  PartnerFinSobData: (data.PartnerFinSobData ?? []).flatMap(
    ({ PartnerId, Offering, PartnerFinSobData: rows }) =>
      (rows ?? []).map((row) =>
        mapPartnerFinSobRowFromApi(row, { partnerId: PartnerId, offering: Offering })
      )
  ),
  CommitmentData: data.CommitmentData ?? [],
  CompletenessData: data.CompletenessData ?? [],
  CommitmentStatus: data.CommitmentStatus ?? [],
  PartnerData: (data.PartnerData ?? []).map(mapCockpitPartnerFromApi),
  ActualsAndTargetsSettings: data.ActualsAndTargetsSettings ?? [],
  MDFActivities: (data.MDFActivities ?? []).map(({ PartnerId, MDFActivities }) => ({
    PartnerId,
    MDFActivities: (MDFActivities ?? []).map(mapCockpitMdfActivityFromApi),
  })),
  MDFSales: (data.MDFSales ?? []).flatMap(({ MDFSales }) => MDFSales ?? []),
  KeyInitiatives: deduplicateKeyInitiatives(data.KeyInitiatives ?? []),
  PBMActions: data.PBMActions ?? [],
  Users: data.Users ?? [],
  Offerings: parseCockpitProgramOfferings(data.PartnerData?.[0]?.ProgramOfferingsJson ?? '[]'),
  PartnerOfferingsCatalog: getPartnerOfferingsCatalog(data.PartnerData ?? []),
  AvailableCurrencies: getAvailableCockpitCurrencies(
    data.PartnerData?.[0]?.LocalCurrency,
    data.PartnerData?.[0]?.DefaultCurrency
  ),
  ELTDocuments: Array.isArray(data.ELTDocuments) ? data.ELTDocuments : [],
  QBRDocuments: Array.isArray(data.QBRDocuments) ? data.QBRDocuments : [],
  PartnerFlags: data.PartnerFlags ?? [],
  PartnerFlagRequests: (data.PartnerFlagRequests ?? []).flatMap(
    ({ PartnerFlagRequests }) => PartnerFlagRequests ?? []
  ),
  PartnerFlagApprovers: (data.PartnerFlagApprovers ?? []).map(({ Approver }) => Approver),
  AttentionDict: data.AttentionDict ?? [],
})

const fetchFinancialCockpitDataWithCache = () => {
  const cache = new Map<string, IFinancialCockpitData>()

  const fetcher = async ({
    fYear,
    partnerId,
    shouldResetCache = false,
    silent = false,
    dispatch,
    debug = false,
  }: IFetchFinancialCockpitDataArgs): Promise<IFinancialCockpitData> => {
    const cacheKey = `${fYear}-${partnerId ?? ''}`
    const cachedData = cache.get(cacheKey)
    if (cachedData && !shouldResetCache) {
      return cachedData
    }

    const serviceUrl = settings.httpsServices.partner360
    const url = `${serviceUrl}/api/v0/planningCockpit/planningCockpitGzip`
    const [accessToken] = getTokens()
    const body = { fYear, partnerId }
    const recorder = createNetworkDebugRecorder({
      enabled: debug,
      name: 'planningCockpitGzip',
      method: 'POST',
      url,
      body,
      headers: { 'Content-Type': 'application/json' },
      target: 'state.partner360.financialCockpit',
      silent,
    })
    let stage = 'fetch'
    let status: number | null = null
    let buffer: ArrayBuffer | null = null

    try {
      if (!silent) {
        dispatch(requestingStarted())
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      })
      status = response.status
      recorder.step('waiting')

      if (silent && !response.ok) {
        throw new Error(`Request failed with status: ${response.status}`)
      }

      stage = 'download'
      buffer = silent
        ? await response.arrayBuffer()
        : await dispatch(downloadWithProgress({ response, onProgress: recorder.progress })).unwrap()
      recorder.step('download')

      if (!silent) {
        dispatch(processingStarted())
      }

      stage = 'pako.ungzip'
      const jsonString = pako.ungzip(buffer, { to: 'string' })
      recorder.step('pako.ungzip')
      stage = 'JSON.parse'
      const result = JSON.parse(jsonString) as IFinancialCockpitResponse
      recorder.step('JSON.parse')

      stage = 'mapFinancialCockpitDataFromApi'
      const mapped = mapFinancialCockpitDataFromApi(result.data)
      recorder.step('mapFinancialCockpitDataFromApi')
      cache.set(cacheKey, mapped)

      if (!silent) {
        dispatch(resetDownload())
      }

      recorder.finish({
        status,
        gzipBytes: buffer.byteLength,
        jsonBytes: jsonString.length,
        raw: result,
        mapped,
      })
      recorder.measureStoreUpdate()

      return mapped
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to fetch financial cockpit data'

      Logger.error(e)

      recorder.fail({
        status,
        gzipBytes: buffer?.byteLength ?? null,
        mapped: EMPTY_FINANCIAL_COCKPIT_DATA,
        message: errorMessage,
        stage,
        responseText: readNetworkDebugText(buffer),
      })

      if (!silent) {
        dispatch(downloadFailed({ error: errorMessage }))
      }

      NotificationController.showError({ message: errorMessage })

      return EMPTY_FINANCIAL_COCKPIT_DATA
    }
  }

  fetcher.clearCache = () => cache.clear()

  return fetcher
}

export const fetchFinancialCockpitData = fetchFinancialCockpitDataWithCache()
